/**
 * Utility functions for handling role-to-company mappings
 */

/**
 * Convert a role name to a company name (always lowercase)
 * Handles various role formats:
 * - "norse" -> "norse"
 * - "Norse" -> "norse"
 * - "NORSE_ADMIN" -> "norse"
 * - "coop_admin" -> "coop"
 */
export const roleToCompany = (role: string): string => {
  // Remove '_admin' suffix if present and convert to lowercase
  const company = role.replace(/_admin$/, '').toLowerCase();
  
  // Log the conversion for debugging
  console.log(`🔄 Role to Company: "${role}" -> "${company}"`);
  
  return company;
};

/**
 * Get company name with proper capitalization for display
 */
export const getCompanyDisplayName = (company: string): string => {
  return company.charAt(0).toUpperCase() + company.slice(1);
};

/**
 * Get the first company from a list of roles
 */
export const getFirstCompanyFromRoles = (roles: string[]): string | null => {
  console.log('🎯 Getting first company from roles:', roles);
  
  if (!roles || roles.length === 0) {
    console.warn('⚠️ No roles provided to getFirstCompanyFromRoles');
    return null;
  }
  
  const firstRole = roles[0];
  const company = roleToCompany(firstRole);
  
  console.log(`✅ First company determined: "${company}" from role: "${firstRole}"`);
  return company;
};

/**
 * Validate if a role is an admin role
 * Any role is considered valid for creating a company route
 */
export const isAdminRole = (role: string): boolean => {
  // Any role is valid - we don't need to be restrictive here
  // The role itself determines the company access
  const isAdmin = role.length > 0; // Any non-empty role is valid
  
  console.log(`🔐 Role "${role}" is valid admin role: ${isAdmin}`);
  return isAdmin;
};

/**
 * Get all admin roles from a list of roles
 */
export const getAdminRoles = (roles: string[]): string[] => {
  const adminRoles = roles.filter(isAdminRole);
  console.log('👑 Admin roles found:', adminRoles);
  return adminRoles;
};

/**
 * Create a lowercase URL-safe version of a string
 */
export const createUrlSafeString = (str: string): string => {
  return encodeURIComponent(str.toLowerCase());
};

/**
 * Decode a URL parameter and restore original case by comparing with available options
 * This is needed because URLs are lowercase but API expects original case
 */
export const decodeUrlParam = (urlParam: string): string => {
  return decodeURIComponent(urlParam);
};